"use client";
import { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useDispatch } from "react-redux";

import { loginReduxState } from "@/src/store/auth";

import { login, sendActivationLink } from "@/src/services/auth";

import validate from "helpers/form-validation";
import { hashWithMd5 } from "helpers/hash";
import getMessage from "helpers/error-messages";
import { Ga4WithEventParameters } from "helpers/google-tag-manager/events/ga4-with-eventparameters";
import { gtmLoadInterval } from "helpers/google-tag-manager/gtm-load-interval";
import { setCookie } from "@/src/helpers/cookie";

import { HASHED_MAIL } from "@/src/utils/constants/cookie-keys";

import Input from "components/common/input";
import Button from "components/common/button";
import ModalTextContent from "components/modal-text-content";
import ActivationModal from "@/src/components/login/activation-modal";
import InformationModal from "src/components/login/information-modal";
import ExplicitConsent from "@/modal-contents/explicit-consent";
import PassiveAccountReasonModal from "src/components/login/passive-account-reason-modal";

import styles from "./styles.module.css";
import Modal from "components/common/modal";

const DynamicPhoneConfirmationModal = dynamic(() => import("components/common/phone-confirmation"), { ssr: false });
const DynamicModal = dynamic(() => import("components/common/modal"), { ssr: false });

const LoginForm = ({
  modal,
  handleForgotButton,
  successCallback,
  isUserActivationPage,
  isUserActivationSuccess,
  searchParams,
}) => {
  const [form, setForm] = useState({
    email: "",
    password: "",
    passwordRepeat: "",
    mobilePhone: isUserActivationSuccess?.result?.phoneNumber || "",
  });

  const [errors, setErrors] = useState({
    email: {
      value: null,
      message: null,
    },
    password: {
      value: null,
      message: null,
    },
    passwordRepeat: {
      value: null,
      message: null,
    },
    wrongUser: {
      value: null,
      message: null,
    },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccessModal, setIsSuccessModal] = useState(false);
  const [showHide, setShowHide] = useState(false);
  const [showHideRepeatPassword, setShowHideRepeatPassword] = useState(false);
  const [isFormSending, setIsFormSending] = useState(false);
  const [isVisibleInformationModal, setIsVisibleInformationModal] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [isPassiveAccountReasonModalStatus, setIsPassiveAccountReasonModalStatus] = useState({
    isOpen: false,
    message: "",
  });
  const [mobilePhoneVerificationModalVisible, setMobilePhoneVerificationModalVisible] = useState(false);

  const dispatch = useDispatch();
  const [confirmationStep, setConfirmationStep] = useState("VERIFICATION");

  const informationModal = classNames({
    [styles.infoWrapper]: true,
    [styles.isHidden]: contractModalOpen,
  });

  const handleInputOnChange = (value, type) => {
    if (type === "password") {
      if (value.length > 0) {
        setShowHide(true);
      } else {
        setShowHide(false);
      }
    }

    if (type === "passwordRepeat") {
      if (value.length > 0) {
        setShowHideRepeatPassword(true);
      } else {
        setShowHideRepeatPassword(false);
      }
    }

    setForm({
      ...form,
      [type]: value,
    });
  };

  const handleInputFocus = (e, type) => {
    setErrors({ ...errors, [type]: { value: false, message: null }, wrongUser: { value: false, message: null } });
  };

  const handleClick = async (e) => {
    e.preventDefault();
    localStorage.setItem("related-login-sent", "false");
    setIsFormSending(true);
    const hasError = await validateForm();
    if (hasError) {
      setIsFormSending(false);
      return null;
    }
    setIsSuccessModal(false);

    if (!isUserActivationPage) {
      try {
        const hashPassword = hashWithMd5(form.password);
        const response = await login({
          email: form.email,
          password: hashPassword,
          encrypted: true,
        });
        const responseData = await response.data;

        const {
          id,
          fullName,
          accountId,
          accountName,
          emailAddress,
          mobilePhone,
          accountType,
          userType,
          openExplictConsentContract,
          role,
          hashedEmail,
        } = responseData?.result;

        Ga4WithEventParameters({
          eventName: "login",
          eventParameters: {
            method: "email",
            user_id: id,
          },
        });

        const authorization = response.headers.authorization;
        const refreshToken = response.headers["x-refresh-token"];
        const result = {
          id,
          fullName,
          accountId,
          accountName,
          emailAddress,
          mobilePhone,
          accountType,
          userType,
          role,
          Authorization: authorization,
          "X-Refresh-Token": refreshToken,
        };

        setCookie(HASHED_MAIL, hashedEmail, 365);

        await localStorage.setItem("user-data", JSON.stringify(result));
        await localStorage.setItem("Authorization", authorization);
        await localStorage.setItem("RefreshToken", refreshToken);
        dispatch(loginReduxState());

        if (successCallback) {
          successCallback();
        } else if (openExplictConsentContract) {
          setIsVisibleInformationModal(true);
        } else {
          window.location.href = "/";
        }
      } catch (error) {
        const errorResponse = error?.response?.data;

        if (error?.response?.data?.status === "error") {
          const message = getMessage(errorResponse);
          const { code } = message;
          const isHaveAccountActivationError = code === "4220002";
          const isHaveReasonForAccountPassive = code === "4000004";

          if (isHaveAccountActivationError) {
            setIsModalOpen(true);
            setIsFormSending(false);
          } else if (isHaveReasonForAccountPassive) {
            const { description } = errorResponse || {};
            setIsPassiveAccountReasonModalStatus({
              isOpen: true,
              message: description,
            });
            setIsFormSending(false);
          } else {
            setErrors({
              ...errors,
              wrongUser: {
                value: true,
                message: message?.error?.description?.tr,
              },
            });
            setIsFormSending(false);
          }
        } else {
          setErrors({
            ...errors,
            wrongUser: {
              value: true,
              message: "E-posta adresiniz veya şifreniz hatalı.",
            },
          });
          setIsFormSending(false);
        }
      }
    } else {
      setIsFormSending(false);
      setMobilePhoneVerificationModalVisible(true);
    }
  };

  const validateForm = async () => {
    const fieldErrors = await validate({
      email: form?.email === "" ? null : form?.email,
      passwordForSignin: form?.password === "" ? null : form?.password,
    });
    const errorEmail = fieldErrors?.email;
    const errorPass =
      fieldErrors?.passwordForSignin || (form.password.length < 6 ? "Şifre en az 6 karakter olmalıdır." : null);
    const errorPasswordRepeat = isUserActivationPage
      ? form.password !== form.passwordRepeat
        ? "Şifreler aynı olmalı."
        : form.passwordRepeat.length < 6
        ? "Şifre en az 6 karakter olmalıdır."
        : null
      : null;

    setErrors({
      ...errors,
      email: isUserActivationPage
        ? {
            value: null,
            message: null,
          }
        : {
            value: !!errorEmail,
            message: errorEmail,
          },
      password: {
        value: !!errorPass,
        message: errorPass,
      },
      passwordRepeat: isUserActivationPage
        ? {
            value: !!errorPasswordRepeat,
            message: errorPasswordRepeat,
          }
        : {
            value: null,
            message: null,
          },
    });

    return !!((!isUserActivationPage && errorEmail) || errorPass || (isUserActivationPage && errorPasswordRepeat));
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalButtonClick = async () => {
    try {
      await sendActivationLink({ email: form.email, password: form.password });
      setIsSuccessModal(true);
    } catch (e) {
      console.error(e);
    }
  };

  const handleForgotPasswordButton = async (e) => {
    e.preventDefault();
    await localStorage.setItem("forgot-password-user", form?.email);

    if (modal) {
      handleForgotButton();
    } else {
      window.location.href = "/sifremi-unuttum";
    }
  };

  const handleKeyPress = (e) => {
    if (e.code === "Enter") {
      handleClick(e);
    }
  };

  const handlePassiveAccountReasonModalClose = () => {
    setIsPassiveAccountReasonModalStatus({
      isOpen: false,
      message: "",
    });
  };

  useEffect(() => {
    if (isUserActivationSuccess?.result?.phoneNumber) {
      setForm((prevForm) => ({
        ...prevForm,
        mobilePhone: isUserActivationSuccess.result.phoneNumber,
      }));
    }
  }, [isUserActivationSuccess?.result?.phoneNumber]);

  useEffect(() => {
    gtmLoadInterval(() => {
      Ga4WithEventParameters({
        eventName: "form",
        eventParameters: {
          label: "view||login-form",
        },
      });
    });
  }, []);

  return (
    <div className={styles.wrapper}>
      <DynamicModal isOpen={isModalOpen} handleClose={() => handleModalClose()} zIndex={111}>
        <ModalTextContent>
          <ActivationModal handleClick={handleModalButtonClick} success={isSuccessModal} />
        </ModalTextContent>
      </DynamicModal>
      <div className={informationModal}>
        <DynamicModal
          isOpen={isVisibleInformationModal}
          handleClose={() => setIsVisibleInformationModal(false)}
          onlyCloseClickedButton
        >
          <InformationModal setContractModalOpen={setContractModalOpen} />
        </DynamicModal>
      </div>
      {/* This component works within the INFORMATION modal. */}
      <DynamicModal isOpen={contractModalOpen} handleClose={() => setContractModalOpen(false)}>
        <ModalTextContent>
          <ExplicitConsent />
        </ModalTextContent>
      </DynamicModal>
      <DynamicModal
        isOpen={isPassiveAccountReasonModalStatus.isOpen}
        handleClose={() => handlePassiveAccountReasonModalClose()}
        zIndex={111}
      >
        <PassiveAccountReasonModal
          message={isPassiveAccountReasonModalStatus.message}
          closeModal={handlePassiveAccountReasonModalClose}
        />
      </DynamicModal>
      <Modal
        isOpen={mobilePhoneVerificationModalVisible}
        handleClose={() => {
          setMobilePhoneVerificationModalVisible(false);
        }}
        zIndex={111}
      >
        <DynamicPhoneConfirmationModal
          form={form}
          setForm={setForm}
          confirmationStep={confirmationStep}
          setConfirmationStep={setConfirmationStep}
          isUserActivationPage={true}
          isUserActivationSuccess={isUserActivationSuccess}
          searchParams={searchParams}
        />
      </Modal>
      <div className={styles.formWrapper}>
        <form onSubmit={(e) => handleClick(e)}>
          <h3 className={styles.title}>{isUserActivationPage ? "Şifre Oluştur" : "Giriş Yap"} </h3>
          {errors.wrongUser?.value && <span className={styles.wrongUser}>{errors?.wrongUser?.message}</span>}
          {isUserActivationSuccess?.result?.emailAddress && isUserActivationPage && (
            <p>
              {isUserActivationSuccess?.result?.emailAddress} mail adresinize ait şifrenizi aşağıda belirleyebilirsiniz.
            </p>
          )}
          {!isUserActivationPage && (
            <div>
              <label htmlFor="email" className={styles.inputTitle}>
                E-posta Adresi
              </label>
              <div className={styles.mailInput}>
                <Input
                  errorMessage={errors?.email?.message}
                  inputClass={styles.loginInput}
                  autoComplete="username"
                  type="text"
                  id="email"
                  name="email"
                  onChange={(e) => handleInputOnChange(e?.target?.value, "email")}
                  onFocus={(e) => handleInputFocus(e?.target?.value, "email")}
                  onKeyPress={handleKeyPress}
                  placeholder="E-posta Adresinizi Giriniz"
                />
              </div>
            </div>
          )}
          <div className={styles.passwordField}>
            <label htmlFor="password" className={styles.inputTitle}>
              Şifre
            </label>
            <div className={styles.mailInput}>
              <Input
                password
                showHideButton={showHide}
                inputClass={styles.loginInput}
                autoComplete="current-password"
                id="password"
                name="password"
                errorMessage={errors?.password?.message}
                onChange={(e) => handleInputOnChange(e?.target?.value, "password")}
                onFocus={(e) => handleInputFocus(e?.target?.value, "password")}
                onKeyPress={handleKeyPress}
                placeholder="Şifrenizi Giriniz"
              />
            </div>
          </div>
          {isUserActivationPage && (
            <>
              <div className={styles.passwordField}>
                <label htmlFor="passwordRepeat" className={styles.inputTitle}>
                  Şifre (Tekrar)
                </label>
                <div className={styles.mailInput}>
                  <Input
                    password
                    showHideButton={showHideRepeatPassword}
                    inputClass={styles.loginInput}
                    autoComplete="current-password-password"
                    id="passwordRepeat"
                    name="passwordRepeat"
                    errorMessage={errors?.passwordRepeat?.message}
                    onChange={(e) => handleInputOnChange(e?.target?.value, "passwordRepeat")}
                    onFocus={(e) => handleInputFocus(e?.target?.value, "passwordRepeat")}
                    onKeyPress={handleKeyPress}
                    placeholder="Tekrar Şifre Giriniz"
                  />
                </div>
              </div>
            </>
          )}
          {!isUserActivationPage && (
            <div className={styles.forgetPassword}>
              <button onClick={handleForgotPasswordButton} className={styles.forgetPasswordLink}>
                Şifremi Unuttum
              </button>
            </div>
          )}
          <div className={styles.loginButton}>
            <Button size="M" type="submit" onClick={(e) => handleClick(e)} isLoading={isFormSending}>
              {isUserActivationPage ? "Devam" : "Giriş Yap"}
            </Button>
          </div>
          {!isUserActivationPage && (
            <div className={styles.signUp}>
              <p className={styles.signUpQuestion}>Üyeliğin mi yok?</p>
              <span>
                <a href="/uye-ol/bireysel" className={styles.forgetPasswordLink}>
                  Üye Ol
                </a>
              </span>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

LoginForm.propTypes = {
  modal: PropTypes.bool,
  handleForgotButton: PropTypes.func,
  successCallback: PropTypes.func,
  isUserActivationPage: PropTypes.bool,
  isUserActivationSuccess: PropTypes.object,
  searchParams: PropTypes.object,
};

export default LoginForm;
